import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { getScrollTop, getScrollEventTarget, scrollTo } from '@theme/scroll-container';

/**
 * @typedef {Object} BackToTopRefs
 * @property {HTMLButtonElement} button - The floating back-to-top button
 */

/**
 * A custom element that shows a floating button once the shopper has scrolled
 * past one viewport height, and scrolls back to the top of the page on click.
 *
 * @extends {Component<BackToTopRefs>}
 */
class BackToTopComponent extends Component {
  requiredRefs = ['button'];

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {boolean} */
  #isVisible = false;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  connectedCallback() {
    super.connectedCallback();

    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#updateVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    if (this.#scrollRafId !== null) cancelAnimationFrame(this.#scrollRafId);
  }

  /**
   * Scrolls the page back to the top.
   */
  scrollToTop = () => {
    scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  };

  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateVisibility();
    });
  };

  #updateVisibility() {
    // Half a viewport, not a full one — on shorter pages the total scrollable
    // distance can be less than `window.innerHeight`, which would make a
    // full-viewport threshold unreachable even after scrolling to the bottom.
    const shouldBeVisible = getScrollTop() > window.innerHeight * 0.5;
    if (shouldBeVisible === this.#isVisible) return;

    this.#isVisible = shouldBeVisible;
    const { button } = this.refs;

    button.dataset.visible = String(shouldBeVisible);
    if (shouldBeVisible) {
      button.removeAttribute('aria-hidden');
      button.removeAttribute('tabindex');
    } else {
      button.setAttribute('aria-hidden', 'true');
      button.setAttribute('tabindex', '-1');
    }
  }
}

if (!customElements.get('back-to-top-component')) {
  customElements.define('back-to-top-component', BackToTopComponent);
}
